import type { SanityMediaValue } from "@ouster/shared";
import React from "react";

import { toCraftMedia } from "~/features/sanity/media";
import { type Ratio } from "~/features/ui/aspect-ratio";
import { Media } from "~/features/ui/media";
import { VideoControls } from "~/features/ui/video/video-controls";

type Props = {
  fit?: "cover" | "fill";
  height?: number;
  media?: SanityMediaValue | null;
  ratio?: Ratio;
  /** Captions are useful in long-form content, but not in fixed-height card media. */
  showCaption?: boolean;
  showExternalVideoControls?: boolean;
  sizes?: string;
};

const supportedRatios = new Set([
  "16/9",
  "5/6",
  "4/3",
  "2/1",
  "3/2",
  "1/1",
  "490/400",
  "310/260",
  "496/280",
  "496/540",
]);

export function toSupportedRatio(value?: string | null): Ratio | undefined {
  return value && supportedRatios.has(value) ? (value as Ratio) : undefined;
}

export function hasRenderableSanityMedia(media?: SanityMediaValue | null): media is SanityMediaValue {
  return Boolean(toCraftMedia(media));
}

export function SanityMediaFigure({
  fit = "fill",
  height,
  media,
  ratio,
  showCaption = true,
  showExternalVideoControls,
  sizes,
}: Props) {
  const craftMedia = toCraftMedia(media);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  if (!craftMedia) {
    return null;
  }

  return (
    <Media
      {...craftMedia}
      showCaption={showCaption}
      imageProps={{ fit, height, ratio, sizes }}
      inlineVideoProps={{
        fit,
        height,
        ratio,
        videoRef,
        controls: <VideoControls videoRef={videoRef} tw="absolute top-16 right-16 z-10" />,
      }}
      externalVideoProps={{
        fit,
        ratio,
        ...(showExternalVideoControls === undefined ? {} : { showControls: showExternalVideoControls }),
      }}
      audioProps={{
        fit,
      }}
    />
  );
}
